import { useEffect, useCallback } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import { useSupportChatStore } from '../supportChatStore.ts';

import { initSupportChat, type InitSupportChatOptions } from './initSupportChat.ts';

import { type SupportChatTouchpoint } from '../types.ts';
import { type useSupportChat_supportChat$key } from './__generated__/useSupportChat_supportChat.graphql.ts';
import { type useSupportChat_user$key } from './__generated__/useSupportChat_user.graphql.ts';
import { type useSupportChat_seller$key } from './__generated__/useSupportChat_seller.graphql.ts';

type EndUserUnion =
    | {
          user: useSupportChat_user$key | null | undefined;
          seller?: undefined;
      }
    | {
          seller: useSupportChat_seller$key | null | undefined;
          user?: undefined;
      };

type UseSupportChatInitOptions = Omit<
    InitSupportChatOptions,
    'environment' | 'user' | 'seller' | 'isSellerChat'
>;

const supportChatFragment = graphql`
    fragment useSupportChat_supportChat on SupportChatQuery {
        touchpoint
        isLiveAgentAvailable
    }
`;

const userFragment = graphql`
    fragment useSupportChat_user on User {
        ...initSupportChat_user
    }
`;

const sellerFragment = graphql`
    fragment useSupportChat_seller on Seller {
        ...initSupportChat_seller
    }
`;

function useSupportChat({
    supportChat: supportChatRef,
    user: userRef,
    seller: sellerRef,
}: {
    supportChat: useSupportChat_supportChat$key | null | undefined;
} & EndUserUnion): {
    isResolved: boolean;
    isEnabled: boolean;
    isChatAvailable: boolean;
    isLiveAgentAvailable: boolean;
    hasActiveChat: boolean;
    /**
     * Called to initialize the support chat. Subsequent calls if
     * chat was already initialized will update the pre chat fields and open
     * the chat window.
     */
    initSupportChat: (options: UseSupportChatInitOptions) => void;
} {
    const environment = useRelayEnvironment();
    const supportChat = useFragment(supportChatFragment, supportChatRef);
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const touchpoint = supportChat?.touchpoint;
    const isLiveAgentAvailable = supportChat?.isLiveAgentAvailable;

    const isStoreEnabled = useSupportChatStore(state => state.isEnabled);
    const chatType = useSupportChatStore(state => state.chatType);
    const hasActiveChat = useSupportChatStore(state => state.hasActiveChat);
    const isChatAvailable = chatType === 'SALESFORCE' ? !!isLiveAgentAvailable : true;
    const isEnabled = !!isStoreEnabled && isChatAvailable;

    const initChat = useCallback(
        (options: UseSupportChatInitOptions) => {
            initSupportChat({
                environment,
                user,
                seller,
                ...options,
            });
        },
        [environment, user, seller]
    );

    useEffect(() => {
        if (!chatType) {
            return;
        }

        const store = useSupportChatStore.getState();

        store.setShouldMount(true);

        const hasActiveSalesforceChat =
            store.chatType === 'SALESFORCE' &&
            store.hasActiveChat &&
            store.touchpoint === touchpoint;

        if (hasActiveSalesforceChat) {
            return;
        }

        if (touchpoint) {
            store.setTouchpoint(touchpoint as SupportChatTouchpoint);
        }

        if (typeof isLiveAgentAvailable === 'boolean') {
            store.setIsLiveAgentAvailable(isLiveAgentAvailable);
        }
    }, [chatType, isStoreEnabled, touchpoint, isLiveAgentAvailable]);

    return {
        isResolved: isStoreEnabled !== null,
        isEnabled,
        isChatAvailable,
        isLiveAgentAvailable: !!isLiveAgentAvailable,
        hasActiveChat,
        initSupportChat: initChat,
    };
}

export { useSupportChat };
